import { NotificationType, useTranslation } from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { ButtonIcon, ButtonVariant, Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import { Spacer } from "@nextui-org/react";
import { useEffect, useState } from "react";
import { useRouteError } from "react-router-dom";

import { getOfflineNotificationMessage } from "@/components/common/Notification/utils/notification";
import { Row, SpicyButton, Text } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION } from "@/constants";
import useToast from "@/hooks/useToast";

const logger = appLogger.withTag("RootErrorBoundary");

const RootErrorBoundary = () => {
  const applicationId = basicAppConfig.applicationId;
  const error = useRouteError();
  const { displayToast } = useToast();
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  function hardReloadWithCacheBusting() {
    const uniqueToken = new Date().getTime();
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set("cacheBust", uniqueToken);
    window.location.href = newUrl.href;
  }

  useEffect(() => {
    if (!navigator.onLine) {
      displayToast({ message: getOfflineNotificationMessage(t), type: NotificationType.warning });
    }
    logger.error("Error in RootErrorBoundary", error);
  }, [displayToast, error, t]);

  const renderErrorMessage = () => {
    if (!navigator.onLine) {
      return <Trans i18nKey="errorBoundary:message.offline" />;
    } else if (error && error.message && error.message.startsWith("LazyLoadError")) {
      return <Trans i18nKey="errorBoundary:message.newVersion" />;
    } else {
      return <Trans i18nKey="errorBoundary:message.generic" />;
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.href} ( ${error?.message} )`);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      logger.error("Failed to copy error message", error);
    }
  };

  return (
    <div className="p-4 px-8">
      <h1>{renderErrorMessage()}</h1>
      <Spacer y={2} />
      <Row className="gap-3">
        <SpicyButton variant="bordered" color="transparent" onClick={hardReloadWithCacheBusting}>
          <Trans i18nKey="errorBoundary:cta.reload" />
        </SpicyButton>
        <SpicyButton as={LocalizedLink} to="/" variant="secondary" color="primary_strong">
          <Trans i18nKey="errorBoundary:cta.goBack" />
        </SpicyButton>
      </Row>
      <Spacer y={6} />
      <Text variant="xs" type="neutral">
        <Trans
          i18nKey="errorBoundary:debugInfo.label"
          components={{
            supportLink: (
              <LocalizedLink
                to={`${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId]?.docs}/support`}
                target="_blank"
                className="underline"
                aria-label="support"
              />
            ),
          }}
        />
        <br />
        <br />
        <Trans i18nKey="errorBoundary:debugInfo.detail" values={{ url: window.location.href, errorMessage: error?.message }} />
      </Text>{" "}
      {isCopied ? (
        <Icon icon={IconVariants.CHECK} size={IconSize.XSMALL} className="text-gray-800 relative top-[3px] left-1" />
      ) : (
        <ButtonIcon
          icon={IconVariants.COPY}
          variant={ButtonVariant.TRANSPARENT}
          size={IconSize.XSMALL}
          onClick={handleCopy}
          className="cursor-pointer text-gray-500 hover:text-gray-800 relative top-[3px] left-1"
        />
      )}
    </div>
  );
};

export default RootErrorBoundary;
